import React from "react";
import { RenderElementProps } from "slate-react";
import { CustomElement } from "../types";
import { getAlignmentClass } from "../utils/text";
import { AIContentNode, AIContentElement } from "./AIContentNode";

// Define animated text gradient classes - ensure 'animate-shimmer' is configured in Tailwind
const loadingEffectClasses =
  "animate-[text-gradient_1s_linear_infinite] animate-pulse bg-gradient-to-r from-teal-500 via-purple-500 to-orange-500 bg-clip-text text-transparent";
export const Element = ({
  attributes,
  children,
  element,
  isLoading, // isLoading prop controls the effect
}: RenderElementProps & { isLoading: boolean }) => {
  const el = element as CustomElement;
  const alignClass = el.align ? getAlignmentClass(el.align) : "";

  // Add loading classes conditionally
  const loadingClasses = isLoading ? loadingEffectClasses : "";

  // Base props shared by all elements
  const commonProps = {
    ...attributes,
    element, // Pass element down for Slate context if needed
  };

  switch (el.type) {
    case "heading-one":
      const h1Classes = `text-3xl font-bold py-2 mb-4 ${alignClass} ${loadingClasses}`;
      return (
        <h1 {...commonProps} className={h1Classes}>
          {children}
        </h1>
      );
    case "heading-two":
      const h2Classes = `text-2xl font-semibold py-2 mb-3 ${alignClass} ${loadingClasses}`;
      return (
        <h2 {...commonProps} className={h2Classes}>
          {children}
        </h2>
      );
    case "heading-three":
      const h3Classes = `text-xl font-semibold py-1 mb-2 ${alignClass} ${loadingClasses}`;
      return (
        <h3 {...commonProps} className={h3Classes}>
          {children}
        </h3>
      );
    case "heading-four":
      const h4Classes = `text-lg font-semibold py-1 mb-2 ${alignClass} ${loadingClasses}`;
      return (
        <h4 {...commonProps} className={h4Classes}>
          {children}
        </h4>
      );
    case "heading-five":
      const h5Classes = `text-base font-semibold py-1 mb-2 ${alignClass} ${loadingClasses}`;
      return (
        <h5 {...commonProps} className={h5Classes}>
          {children}
        </h5>
      );
    case "heading-six":
      const h6Classes = `text-sm font-semibold py-1 mb-2 ${alignClass} ${loadingClasses}`;
      return (
        <h6 {...commonProps} className={h6Classes}>
          {children}
        </h6>
      );
    case "bulleted-list":
      const ulClasses = `list-disc list-inside py-1 mb-2 ${alignClass} ${loadingClasses}`;
      return (
        <ul {...commonProps} className={ulClasses}>
          {children}
        </ul>
      );
    case "numbered-list":
      const olClasses = `list-decimal list-inside py-1 mb-2 ${alignClass} ${loadingClasses}`;
      return (
        <ol {...commonProps} className={olClasses}>
          {children}
        </ol>
      );
    case "list-item":
      const liClasses = `py-1 ${alignClass} ${loadingClasses}`;
      return (
        <li {...commonProps} className={liClasses}>
          {children}
        </li>
      );
    case "ai-content":
      // If isLoading, render a placeholder shimmering div instead of AIContentNode
      if (isLoading) {
        return (
          <div
            {...attributes} // Use attributes for slate connection
            className={`py-1 mb-2 ${alignClass} ${loadingClasses}`}
          >
            {"\u00A0"}
          </div>
        );
      }
      // Render AIContentNode normally when not loading
      // Pass only element and children, assuming attributes are handled internally by AIContentNode
      return (
        <div className="mb-2">
          <AIContentNode element={element as AIContentElement}>
            {children}
          </AIContentNode>
        </div>
      );

    default: // paragraph
      const pClasses = `py-1 mb-2 ${alignClass} ${loadingClasses}`;
      return (
        <p {...commonProps} className={pClasses}>
          {children}
        </p>
      );
  }
};
